import { useEffect, useRef } from "react";
import { API_ENDPOINTS } from "../api/api.config";
import { NotificationItem } from "../services/notification.service";

/**
 * Live notification events emitted over Server-Sent Events.
 *
 * Backend pushes one frame per event onto the EventSource stream. Routes
 * (the dispatcher in this hook) decide what to do with each `type`:
 *   - notification:new   → prepend row + bump badge
 *   - notification:read  → drop badge (single or all-read)
 *   - notification:badge_count → out-of-band badge refresh
 *
 * `unread_count` is sent on every event so the badge can be set
 * authoritatively instead of guessing locally.
 */
export interface NotificationStreamEvent {
  type: "notification:new" | "notification:read" | "notification:badge_count";
  notification?: NotificationItem;
  unread_count?: number;
}

/**
 * Subscribe to the live notification stream.
 *
 * Backend route: `GET /notification-srv/stream?access_token=<jwt>` (SSE).
 * EventSource auto-reconnects on transient network failure; the only
 * teardown path is `EventSource.close()` from the effect cleanup.
 *
 * Disabled (no connection opened) when:
 *   - the user isn't authenticated (no `auth_token`),
 *   - the API base URL is missing,
 *   - the `enabled` flag is false (lets a parent suspend the stream).
 *
 * The hook is intentionally event-source agnostic about UI — pass an
 * `onEvent` callback and decide where to put the data.
 */
export function useNotificationStream(
  onEvent: (event: NotificationStreamEvent) => void,
  enabled: boolean = true,
) {
  // Keep the latest callback in a ref so the EventSource doesn't have to
  // re-open every time the parent re-renders with a new lambda.
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;
    const token = localStorage.getItem("auth_token");
    if (!token) return;

    const base = (import.meta as any).env?.VITE_DMS_API || "";
    // VITE_DMS_API typically ends without a trailing slash; the endpoint
    // already starts with `/dms-v2-srv/...`. Concat directly.
    const url = `${base}${API_ENDPOINTS.NOTIFICATION.STREAM}?access_token=${encodeURIComponent(token)}`;

    let es: EventSource | null = null;
    try {
      es = new EventSource(url);
    } catch (err) {
      console.warn("Notification SSE: failed to open EventSource", err);
      return;
    }

    es.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data) as NotificationStreamEvent;
        onEventRef.current(data);
      } catch {
        /* ignore non-JSON frames (keepalive comments etc.) */
      }
    };

    es.onerror = (err) => {
      // EventSource auto-reconnects when state is CONNECTING. Only log so
      // we can see flakey streams in the console; no toast — the bell
      // recovers on the next event.
      // eslint-disable-next-line no-console
      console.debug("Notification SSE error (will auto-reconnect):", err);
    };

    return () => {
      es?.close();
    };
  }, [enabled]);
}

export default useNotificationStream;
